import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PlanPage from "../../entities/PlanPage";
import PlanChangeDialog from "../../components/PlanChangeDialog";
import { planService, subscriptionService } from "../../api";
import AuthService from "../../api/AuthService";
import { useAlert } from "../../context/AlertContext";
import { redirectToCheckout } from "../../utils/checkout";
import LoaderHelper from "../../utils/Loader/LoaderHelper";
import {
  getCurrentPlanId,
  mapApiPlansToCards,
  normalizeId,
  normalizeListResponse,
} from "../../utils/planHelpers";

const Plan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switchingPlanId, setSwitchingPlanId] = useState("");
  const [errorText, setErrorText] = useState("");
  // Country is resolved automatically from the admin's billing/profile country;
  // it is not user-selectable on this screen.
  const [selectedCountry, setSelectedCountry] = useState("");
  const [paymentPreview, setPaymentPreview] = useState(null); // manage-plan payment case
  const [blockedInfo, setBlockedInfo] = useState(null); // DOWNGRADE_BLOCKED case
  // Whether the admin already has an active subscription. Drives create vs
  // manage-plan: a first-ever pick (incl. free tier) MUST use POST /subscription;
  // manage-plan only operates on an existing active subscription.
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const formatCountryForPlanEndpoint = useCallback((country) => {
    return String(country || "").trim();
  }, []);

  const resolveBillingCountry = useCallback(() => {
    try {
      const storedBillingInfo = localStorage.getItem("billingInfo");
      if (storedBillingInfo) {
        const billingCountry = JSON.parse(storedBillingInfo)?.country;
        if (billingCountry && String(billingCountry).trim()) {
          return String(billingCountry).trim();
        }
      }
    } catch (error) {
      // No usable billing country; caller will fall back.
    }
    return "";
  }, []);

  const resolveUserCountry = useCallback(async () => {
    const countryFromStorage =
      localStorage.getItem("country") ||
      localStorage.getItem("companyCountry") ||
      localStorage.getItem("taxCountry");
    if (countryFromStorage && String(countryFromStorage).trim()) {
      return String(countryFromStorage).trim();
    }

    try {
      const accountSettingResult = await AuthService.getAccountSetting();
      const accountCountry = accountSettingResult?.data?.country;
      if (accountSettingResult?.success && accountCountry && String(accountCountry).trim()) {
        const normalizedCountry = String(accountCountry).trim();
        localStorage.setItem("companyCountry", normalizedCountry);
        return normalizedCountry;
      }
    } catch (error) {
      // Try next source.
    }

    try {
      const profileViewResult = await AuthService.getProfileView();
      const profileViewCountry = profileViewResult?.data?.country;
      if (profileViewResult?.success && profileViewCountry && String(profileViewCountry).trim()) {
        const normalizedCountry = String(profileViewCountry).trim();
        localStorage.setItem("companyCountry", normalizedCountry);
        return normalizedCountry;
      }
    } catch (error) {
      // Try next source.
    }

    try {
      const profileResult = await AuthService.getProfile();
      const profileCountry = profileResult?.data?.country;
      if (profileResult?.success && profileCountry && String(profileCountry).trim()) {
        const normalizedCountry = String(profileCountry).trim();
        localStorage.setItem("companyCountry", normalizedCountry);
        return normalizedCountry;
      }
    } catch (error) {
      // Try next source.
    }

    return "";
  }, []);

  const fetchPlans = useCallback(async (overrideCountry) => {
    setLoading(true);
    setErrorText("");
    LoaderHelper.loaderStatus(true);

    try {
      const countryToUse =
        (overrideCountry && String(overrideCountry).trim()) ||
        resolveBillingCountry() ||
        (await resolveUserCountry());
      if (!countryToUse) {
        throw new Error("Country not found in user profile.");
      }
      // Reflect the resolved/selected country in the selector.
      setSelectedCountry(countryToUse);

      const normalizedCountryForRoute = formatCountryForPlanEndpoint(countryToUse);
      if (!normalizedCountryForRoute) {
        throw new Error("Country format is invalid for plans API.");
      }

      const plansResult = await planService.listByCountry(normalizedCountryForRoute);

      const apiPlans = normalizeListResponse(plansResult).filter(
        (plan) => plan?.isDeleted !== true && plan?.isActive !== false
      );

      // planService.listByCountry does not reliably set currentPlan:true per
      // plan, so cross-reference the company's active subscription: the
      // current plan is the one whose subscription.isActive === true AND
      // subscription.planId.isActive === true.
      let fallbackCurrentPlanId = "";
      try {
        const subscriptionsResult = await subscriptionService.list();
        fallbackCurrentPlanId = getCurrentPlanId(
          normalizeListResponse(subscriptionsResult)
        );
      } catch (error) {
        // Non-fatal: current-plan highlighting simply won't show.
      }

      const mappedPlans = mapApiPlansToCards(apiPlans, fallbackCurrentPlanId);
      setPlans(mappedPlans);
      setHasActiveSubscription(
        apiPlans.some((plan) => plan?.currentPlan === true) ||
          Boolean(fallbackCurrentPlanId)
      );
    } catch (error) {
      const message = error?.message || "Unable to load plans.";
      setErrorText(message);
      showAlert(message, "error");
    } finally {
      setLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [formatCountryForPlanEndpoint, resolveBillingCountry, resolveUserCountry, showAlert]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // First-ever pick (no active subscription yet — incl. free-tier activation
  // after registration) → POST /subscription. manage-plan cannot create a first
  // subscription; it only operates on an existing active one.
  const handleFirstSubscription = async (selectedPlanId) => {
    const companyId = normalizeId(
      localStorage.getItem("companyId") || localStorage.getItem("id")
    );
    const adminId = normalizeId(
      localStorage.getItem("userId") || localStorage.getItem("employeeId")
    );

    const result = await subscriptionService.create({
      companyId,
      adminId,
      planId: selectedPlanId,
      date: new Date().toISOString(),
      isActive: true,
    });

    if (result?.success === false) {
      // e.g. incomplete billing info, or "You've already used your one-time
      // free tier. Please choose a paid plan to continue."
      showAlert(result?.message || "Unable to activate plan.", "error");
      return;
    }

    const data = result?.data || {};
    // Paid plan → full-page redirect to Dodo checkout. A free/trial plan returns
    // an internal `redirectUrl` (in-app) rather than an external checkout URL.
    if (data.authorizationUrl) {
      redirectToCheckout(data.authorizationUrl);
      return;
    }

    // Free tier / $0 / trial → activated immediately.
    showAlert(result?.message || "Plan activated successfully.", "success");
    await fetchPlans(selectedCountry);
  };

  // Existing subscription → POST /subscription/manage-plan (upgrade/downgrade/
  // switch). companyId is derived from the JWT server-side, so we only send
  // planId. changeType / paymentRequired / limitViolations drive the UI.
  const handleManagePlan = async (selectedPlanId) => {
    // Billing info must be complete or manage-plan 400s. Gate before the call
    // and send the user to complete it, rather than surfacing a raw error.
    if (localStorage.getItem("billing_info") !== "true") {
      showAlert(
        "Please complete your Billing Information before changing your plan.",
        "info"
      );
      navigate("/dashboard/profile");
      return;
    }

    const result = await subscriptionService.managePlan({ planId: selectedPlanId });

    // HTTP 400: current usage exceeds the target plan's limits.
    if (result?.code === "DOWNGRADE_BLOCKED" || Array.isArray(result?.limitViolations)) {
      setBlockedInfo({
        message: result?.message,
        limitViolations: result?.limitViolations || [],
      });
      return;
    }

    if (result?.success === false) {
      const msg = result?.message || "Unable to change plan.";
      // Backup for the server-side billing gate: send them to complete it.
      if (/billing information/i.test(msg)) {
        showAlert(msg, "info");
        navigate("/dashboard/profile");
        return;
      }
      showAlert(msg, "error");
      return;
    }

    const data = result?.data || {};

    // Payment needed (upgrade proration, or downgrade blocked by outstanding
    // balance): preview the breakdown, then redirect to authorizationUrl.
    if (data.paymentRequired && data.authorizationUrl) {
      setPaymentPreview({ ...data, message: result?.message });
      return;
    }

    // No charge (plan switch / scheduled downgrade / immediate upgrade):
    // confirm, then send the user back to the dashboard.
    showAlert(result?.message || "Plan updated successfully.", "success");
    navigate("/dashboard");
  };

  const handleSwitchPlan = async (plan) => {
    const selectedPlanId = normalizeId(plan?.id);
    if (!selectedPlanId) {
      showAlert("Invalid plan selected.", "error");
      return;
    }

    setSwitchingPlanId(selectedPlanId);
    LoaderHelper.loaderStatus(true);
    try {
      if (hasActiveSubscription) {
        await handleManagePlan(selectedPlanId);
      } else {
        await handleFirstSubscription(selectedPlanId);
      }
    } catch (error) {
      showAlert(error?.message || "Unable to change plan.", "error");
    } finally {
      setSwitchingPlanId("");
      LoaderHelper.loaderStatus(false);
    }
  };

  const emptyText = useMemo(() => {
    if (errorText) return errorText;
    return "No plans available.";
  }, [errorText]);

  return (
    <>
      <PlanPage
        plans={plans}
        isLoading={loading}
        emptyText={emptyText}
        onSwitchPlan={handleSwitchPlan}
        switchingPlanId={switchingPlanId}
      />
      <PlanChangeDialog
        open={Boolean(paymentPreview)}
        mode="payment"
        payment={paymentPreview}
        onClose={() => setPaymentPreview(null)}
      />
      <PlanChangeDialog
        open={Boolean(blockedInfo)}
        mode="blocked"
        blocked={blockedInfo}
        onClose={() => setBlockedInfo(null)}
      />
    </>
  );
};

export default Plan;
